<div
  class="status-container min-vh-100"
  [class.dark-theme]="isDarkTheme"
  [style.--status-primary]="primaryColor">
  @if (isPageInitLoading || isRedirectingToSso) {
    <div class="status-init-loader">
      <div class="status-init-loader-spinner" aria-hidden="true"></div>
      <p>{{ isRedirectingToSso ? 'Redirecting to SSO...' : 'Loading status page...' }}</p>
    </div>
  }

  @if (!isPageInitLoading && !isRedirectingToSso && publicError) {
    <div class="status-init-loader">
      <div class="status-message-card invalid">
        <mat-icon class="material-symbols-outlined">link_off</mat-icon>
        <h2>Invalid Link</h2>
        <p>{{ publicError }}</p>
      </div>
    </div>
  }

  @if (!isPageInitLoading && !isRedirectingToSso && !publicError && (!isSsoProtectedPage || isSsoSessionActive)) {
    <header class="status-topbar">
      <div class="status-shell status-topbar-inner">
        <div class="status-brand-wrap">
          <div class="brand-logo" [style.background-color]="primaryColor">
            @if (logoUrl) {
              <img [src]="logoUrl" alt="Status page logo" class="status-brand-logo-image">
            }
            @if (!logoUrl) {
              <span>{{ (title || domainName || 'S')?.charAt(0)?.toUpperCase() }}</span>
            }
          </div>
          <div class="brand-text">
            <h1>{{ title || (domainName + ' Status') }}</h1>
            <p>{{ description || ('Real-time status updates for all ' + domainName + ' services') }}</p>
          </div>
        </div>
        <div class="status-topbar-actions">
          <button
            type="button"
            class="theme-toggle-btn"
            [class.active]="isDarkTheme"
            [attr.aria-label]="themeToggleLabel"
            [attr.aria-pressed]="isDarkTheme"
            [title]="themeToggleLabel"
            (click)="toggleTheme()">
            <mat-icon class="material-symbols-outlined">{{ isDarkTheme ? 'light_mode' : 'dark_mode' }}</mat-icon>
          </button>
          @if (showSubscribeButton) {
            <span
              [matTooltip]="subscribeButtonTooltip"
              [matTooltipDisabled]="!isSubscribeButtonDisabled"
              matTooltipPosition="below">
              <button
                mat-flat-button
                color="primary"
                class="subscribe-btn"
                [disabled]="isSubscribeButtonDisabled"
                (click)="openSubscribeDialog()">
                <mat-icon class="material-symbols-outlined">notifications_none</mat-icon>
                Subscribe
              </button>
            </span>
          }
          @if (showSsoLogoutButton) {
            <button
              mat-stroked-button
              class="sso-logout-btn"
              [disabled]="isSsoLogoutInProgress"
              (click)="onSsoLogout()">
              <mat-icon class="material-symbols-outlined">logout</mat-icon>
              SSO Logout
            </button>
          }
        </div>
      </div>
    </header>
    <main class="status-shell status-main-content">
      @if (isInternalMode && internalError) {
        <div class="section-empty-state mb-3">
          {{ internalError }}
        </div>
      }
      @if (!isHistoryOnlyView) {
        <div class="overall-banner" [ngClass]="getOverallStatusClass()">
          <mat-icon class="material-symbols-outlined overall-banner-icon">{{ getOverallStatusIcon(overallStatus) }}</mat-icon>
          <span>{{ overallStatus || 'All Systems Operational' }}</span>
        </div>
        <section class="status-section status-section-incidents">
          <div class="section-heading">
            <mat-icon class="heading-icon incident material-symbols-outlined">report_problem</mat-icon>
            <h2>Active Incidents</h2>
          </div>

          @if (incidentsLoading) {
            <div class="section-empty-state">
              Loading active incidents...
            </div>
          }

          @if (!incidentsLoading) {
            @if (activeIncidents.length > 0) {
              <mat-accordion class="status-accordion" multi>
                @for (incident of activeIncidents; track trackByIncidentId($index, incident)) {
                  <mat-expansion-panel
                    class="status-card incident-card"
                    [(expanded)]="incident.expanded">
                    <mat-expansion-panel-header>
                      <mat-panel-title>
                        <div class="status-card-header incident-header">
                          <div class="card-title-row">
                            <div class="status-card-title-main">
                              <span class="card-title">{{ incident?.title || 'Incident' }}</span>
                              <span class="status-pill" [ngClass]="getIncidentPillClass(incident)">
                                <span class="dot"></span>{{ getIncidentStatusLabel(incident) }}
                              </span>
                              @if (getIncidentImpactLabel(incident); as impactLabel) {
                                <span class="status-pill impact-pill incident-impact-tag" [ngClass]="getIncidentImpactClass(incident)">
                                  <span class="dot status-dot"></span>{{ impactLabel }}
                                </span>
                              }
                            </div>
                          </div>
                          @if (!incident.expanded) {
                            <div class="status-card-summary">
                              @if (getIncidentAffectedServiceNames(incident); as affectedServiceNames) {
                                @if (affectedServiceNames.length) {
                                  <div class="meta-tag-list status-card-summary-tags">
                                    @for (serviceName of affectedServiceNames; track trackByText($index, serviceName)) {
                                      <span class="meta-tag">
                                        {{ serviceName }}
                                      </span>
                                    }
                                  </div>
                                }
                              }
                              <div
                                class="incident-preview status-card-summary-description"
                                [innerHTML]="formatRichText(incident?.description, 'No incident details available.') | statusPageRichText"
                                [matTooltip]="getTooltipText(incident?.description, 'No incident details available.')"
                                matTooltipPosition="above">
                              </div>
                              <div class="card-meta status-card-summary-meta">
                                <span><mat-icon class="material-symbols-outlined">schedule</mat-icon>{{ incident?.displayStartedDateTimeWithZone || 'Time unavailable' }}</span>
                              </div>
                            </div>
                          }
                        </div>
                      </mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="card-body">
                      @if (getIncidentAffectedServiceNames(incident); as affectedServiceNames) {
                        @if (affectedServiceNames.length) {
                          <div class="meta-tag-list">
                            @for (serviceName of affectedServiceNames; track trackByText($index, serviceName)) {
                              <span class="meta-tag">
                                {{ serviceName }}
                              </span>
                            }
                          </div>
                        } @else {
                          <span class="meta-tag">No affected services</span>
                        }
                      }
                      <div
                        class="card-description"
                        [innerHTML]="formatRichText(incident?.description, 'No incident details available.') | statusPageRichText"
                        [matTooltip]="getTooltipText(incident?.description, 'No incident details available.')"
                        matTooltipPosition="above">
                      </div>
                      <div class="card-meta">
                        <span><mat-icon class="material-symbols-outlined">schedule</mat-icon>{{ incident?.displayStartedDateTimeWithZone || 'Time unavailable' }}</span>
                      </div>
                      @if (hasIncidentTimeline(incident)) {
                        <div class="timeline-block">
                          <div class="timeline-title">Timeline</div>
                          <div class="timeline-list">
                            @for (update of getIncidentTimeline(incident); track trackByIncidentUpdateId($index, update)) {
                              <div
                                class="timeline-item"
                                [ngClass]="getTimelineUpdateClass(update)">
                                <div class="timeline-dot" [ngClass]="getTimelineUpdateClass(update)"></div>
                                <div class="timeline-body">
                                  <div class="timeline-head">
                                    <span class="timeline-status" [ngClass]="getTimelineUpdateClass(update)">
                                      {{ update?.statusName || update?.status || 'Update' }}
                                    </span>
                                    <span class="timeline-time">
                                      {{ update?.displayTimestampWithZone || 'Time unavailable' }}
                                    </span>
                                  </div>
                                  <div
                                    class="timeline-message"
                                    [class.line-clamp-4]="!incident.expanded"
                                    [innerHTML]="formatRichText(update?.message, 'No update details available.') | statusPageRichText"
                                    [matTooltip]="getTooltipText(update?.message, 'No update details available.')"
                                    [matTooltipDisabled]="incident.expanded"
                                    matTooltipPosition="above">
                                  </div>
                                </div>
                              </div>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </mat-expansion-panel>
                }
              </mat-accordion>
            }
            @if (activeIncidents.length === 0) {
              <div class="section-empty-state">
                No Active Incidents.
              </div>
            }
          }
        </section>
        <section class="status-section status-section-maintenance">
          <div class="section-heading">
            <mat-icon class="heading-icon maintenance material-symbols-outlined">build</mat-icon>
            <h2>Scheduled Maintenance</h2>
          </div>
          @if (maintenancesLoading) {
            <div class="section-empty-state">
              Loading maintenance history...
            </div>
          }
          @if (!maintenancesLoading) {
            @if (maintenances.length > 0) {
              <mat-accordion class="status-accordion">
                @for (maintenance of maintenances; track trackByMaintenanceId($index, maintenance)) {
                  <mat-expansion-panel
                    class="status-card maintenance-card"
                    [(expanded)]="maintenance.expanded">
                    <mat-expansion-panel-header>
                      <mat-panel-title>
                        <div class="status-card-header maintenance-header">
                          <div class="card-title-row">
                            <div class="status-card-title-main">
                              <span class="card-title">{{ maintenance?.title || 'Maintenance Window' }}</span>
                              <span class="status-pill scheduled"><span class="dot"></span>{{ getMaintenanceStatusLabel(maintenance) }}</span>
                            </div>
                          </div>
                          @if (!maintenance.expanded) {
                            <div class="status-card-summary">
                              @if (getMaintenanceAffectedServiceNames(maintenance); as affectedServiceNames) {
                                @if (affectedServiceNames.length) {
                                  <div class="meta-tag-list status-card-summary-tags">
                                    @for (serviceName of affectedServiceNames; track trackByText($index, serviceName)) {
                                      <span class="meta-tag">
                                        {{ serviceName }}
                                      </span>
                                    }
                                  </div>
                                }
                              }
                              <div
                                class="card-description status-card-summary-description"
                                [innerHTML]="formatRichText(maintenance?.description, 'No description available.') | statusPageRichText"
                                [matTooltip]="getTooltipText(maintenance?.description, 'No description available.')"
                                matTooltipPosition="above">
                              </div>
                              <div class="card-meta status-card-summary-meta">
                                @if (isInternalMode) {
                                  <span><mat-icon class="material-symbols-outlined">schedule</mat-icon>{{ getMaintenanceRelativeTime(maintenance) }}</span>
                                  <span><mat-icon class="material-symbols-outlined">calendar_today</mat-icon>{{ maintenance?.displayScheduledDate || 'TBD' }}</span>
                                  <span><mat-icon class="material-symbols-outlined">access_time</mat-icon>{{ maintenance?.displayScheduledTimeRange || 'TBD' }}</span>
                                } @else {
                                  <span><mat-icon class="material-symbols-outlined">calendar_today</mat-icon>{{ maintenance?.displayScheduledDate || 'TBD' }}</span>
                                  <span><mat-icon class="material-symbols-outlined">schedule</mat-icon>{{ maintenance?.displayScheduledTimeRange || 'TBD' }}</span>
                                }
                              </div>
                            </div>
                          }
                        </div>
                      </mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="card-body">
                      @if (getMaintenanceAffectedServiceNames(maintenance); as affectedServiceNames) {
                        @if (affectedServiceNames.length) {
                          <div class="meta-tag-list">
                            @for (serviceName of affectedServiceNames; track trackByText($index, serviceName)) {
                              <span class="meta-tag">
                                {{ serviceName }}
                              </span>
                            }
                          </div>
                        } @else {
                          <span class="meta-tag">No affected services</span>
                        }
                      }
                      <div
                        class="card-description"
                        [class.line-clamp-4]="!maintenance.expanded"
                        [innerHTML]="formatRichText(maintenance?.description, 'No description available.') | statusPageRichText"
                        [matTooltip]="getTooltipText(maintenance?.description, 'No description available.')"
                        [matTooltipDisabled]="maintenance.expanded"
                        matTooltipPosition="above">
                      </div>
                      <div class="card-meta">
                        @if (isInternalMode) {
                          <span><mat-icon class="material-symbols-outlined">schedule</mat-icon>{{ getMaintenanceRelativeTime(maintenance) }}</span>
                          <span><mat-icon class="material-symbols-outlined">calendar_today</mat-icon>{{ maintenance?.displayScheduledDate || 'TBD' }}</span>
                          <span><mat-icon class="material-symbols-outlined">access_time</mat-icon>{{ maintenance?.displayScheduledTimeRange || 'TBD' }}</span>
                        } @else {
                          <span><mat-icon class="material-symbols-outlined">calendar_today</mat-icon>{{ maintenance?.displayScheduledDate || 'TBD' }}</span>
                          <span><mat-icon class="material-symbols-outlined">schedule</mat-icon>{{ maintenance?.displayScheduledTimeRange || 'TBD' }}</span>
                        }
                      </div>
                    </div>
                  </mat-expansion-panel>
                }
              </mat-accordion>
            }
            @if (maintenances.length === 0) {
              <div class="section-empty-state">
                No scheduled maintenance found.
              </div>
            }
          }
        </section>
        <section class="status-section">
          <div class="section-heading service-only">
            <mat-icon class="heading-icon service material-symbols-outlined">dns</mat-icon>
            <h2>Services</h2>
          </div>
          <div class="services-container">
            @if (servicesLoading) {
              @for (row of serviceSkeletonRows; track trackByIndex($index)) {
                <div class="service-card service-skeleton">
                  <div class="service-skeleton-info">
                    <div class="skeleton-line name"></div>
                    <div class="skeleton-line desc"></div>
                  </div>
                  <div class="skeleton-pill"></div>
                </div>
              }
            } @else if (internalServiceGroups.length > 0) {
              <mat-accordion class="service-accordion" multi>
                @for (group of internalServiceGroups; track trackByGroupId($index, group)) {
                  <mat-expansion-panel
                    class="service-card service-card-expandable"
                    [(expanded)]="group.expanded">
                    <mat-expansion-panel-header>
                      <mat-panel-title>
                        <div class="service-info">
                          <div class="service-name">{{ group?.name }}</div>
                        </div>
                      </mat-panel-title>
                      <mat-panel-description class="service-panel-status">
                        <div class="service-status service-status-group" [ngClass]="getStatusClass(group?.effectiveStatusCode, group?.effectiveStatus)">
                          {{ getStatusLabel(group?.effectiveStatus, group?.effectiveStatusCode) }}
                          <span class="dot"></span>
                        </div>
                      </mat-panel-description>
                    </mat-expansion-panel-header>
                    <div class="group-list">
                      @for (service of group?.services; track trackByServiceId($index, service)) {
                        <div class="group-row">
                          <div class="service-info">
                            <div class="service-name service-name-item">{{ service?.name }}</div>
                            @if (service?.description) {
                              <div class="service-desc">{{ service?.description }}</div>
                            }
                          </div>
                          <div class="service-status service-status-item" [ngClass]="getStatusClass(service?.effectiveStatusCode, service?.effectiveStatus)">
                            {{ getStatusLabel(service?.effectiveStatus, service?.effectiveStatusCode) }}
                            <span class="dot"></span>
                          </div>
                        </div>
                      }
                    </div>
                  </mat-expansion-panel>
                }
              </mat-accordion>

              @for (service of services; track trackByServiceId($index, service)) {
                @if (!service?.groups || service?.groups?.length === 0) {
                  <div class="service-card service-card-standalone">
                    <div class="service-main">
                      <div class="service-info">
                        <div class="service-name">{{ service?.name }}</div>
                        <div class="service-desc">{{ service?.description }}</div>
                      </div>
                      <div class="service-status" [ngClass]="getStatusClass(service?.effectiveStatusCode, service?.effectiveStatus)">
                        {{ getStatusLabel(service?.effectiveStatus, service?.effectiveStatusCode) }}
                        <span class="dot"></span>
                      </div>
                    </div>
                  </div>
                }
              }
            } @else {
              @for (service of services; track trackByServiceId($index, service)) {
                @if (!service?.groups || service?.groups?.length === 0) {
                  <div class="service-card service-card-standalone">
                    <div class="service-main">
                      <div class="service-info">
                        <div class="service-name">{{ service?.name }}</div>
                        <div class="service-desc">{{ service?.description }}</div>
                      </div>
                      <div class="service-status" [ngClass]="getStatusClass(service?.effectiveStatusCode, service?.effectiveStatus)">
                        {{ getStatusLabel(service?.effectiveStatus, service?.effectiveStatusCode) }}
                        <span class="dot"></span>
                      </div>
                    </div>
                  </div>
                }
                @if (service?.groups && service?.groups?.length > 0) {
                  <mat-accordion class="service-accordion" togglePosition="before">
                    <mat-expansion-panel class="service-card service-card-expandable">
                      <mat-expansion-panel-header>
                        <mat-panel-title>
                          <div class="service-info">
                            <div class="service-name">{{ service?.name }}</div>
                            <div class="service-desc">{{ service?.description }}</div>
                          </div>
                        </mat-panel-title>
                        <mat-panel-description class="service-panel-status">
                          <div class="service-status" [ngClass]="getStatusClass(service?.effectiveStatusCode, service?.effectiveStatus)">
                            {{ getStatusLabel(service?.effectiveStatus, service?.effectiveStatusCode) }}
                            <span class="dot"></span>
                          </div>
                        </mat-panel-description>
                      </mat-expansion-panel-header>
                      <div class="group-list">
                        @for (group of service.groups; track trackByGroupId($index, group)) {
                          <div class="group-row">
                            <span>{{ group.name }}</span>
                          </div>
                        }
                      </div>
                    </mat-expansion-panel>
                  </mat-accordion>
                }
              }

              @if (services.length === 0) {
                <div class="section-empty-state">
                  No services found.
                </div>
              }
            }
          </div>
        </section>
      }
    </main>
    @if (!isHistoryOnlyView) {
      <div class="status-subfooter">
        <div class="status-shell status-subfooter-inner">
          <a class="history-anchor" [attr.href]="incidentHistoryTabUrl">
            <mat-icon class="material-symbols-outlined">history</mat-icon>
            Incident History ({{ incidentHistoryCountDisplay }})
          </a>
          <span class="powered-by-alertops">Powered by AlertOps</span>
        </div>
      </div>
    }
    @if (isHistoryOnlyView) {
      <section class="status-shell status-section incident-history-section">
        <div class="incident-history-section-header">
          <a
            class="status-history-back-link"
            [attr.href]="statusOverviewUrl"
            aria-label="Back to current status">
            <mat-icon class="material-symbols-outlined">arrow_back</mat-icon>
            <span>Back to Current Status</span>
          </a>
          <div class="section-heading history-section-title">
            <h2>Incident History</h2>
          </div>
        </div>
        <div class="incident-history-filter-row history-filter-row">
          <div class="incident-history-search" (click)="focusIncidentHistorySearchInput()">
            <mat-icon class="incident-history-search-icon material-symbols-outlined">search</mat-icon>
            <input
              #incidentHistorySearchInput
              id="status_main_incident_history_search"
              class="incident-history-search-input"
              type="search"
              [value]="incidentHistorySearchQuery"
              placeholder="Search incidents"
              aria-label="Search incident history"
              (input)="onIncidentHistorySearchInput($event)">
            @if (hasActiveIncidentHistorySearch) {
              <button
                type="button"
                class="incident-history-search-clear"
                aria-label="Clear incident history search"
                matTooltip="Clear search"
                (click)="clearIncidentHistorySearch()">
                <mat-icon class="material-symbols-outlined">close</mat-icon>
              </button>
            }
          </div>
          <button
            type="button"
            class="btn-color filter-btn history-filter-btn"
            [ngClass]="{'btn-active': selectedDateFilter === '7d'}"
            (click)="setDateFilter('7d')">
            7d
          </button>
          <button
            type="button"
            class="btn-color filter-btn history-filter-btn"
            [ngClass]="{'btn-active': selectedDateFilter === '15d'}"
            (click)="setDateFilter('15d')">
            15d
          </button>
          <button
            type="button"
            class="btn-color filter-btn history-filter-btn"
            [ngClass]="{'btn-active': selectedDateFilter === '30d'}"
            (click)="setDateFilter('30d')">
            30d
          </button>

          @if (selectedDateFilter !== 'custom') {
            <button
              type="button"
              class="btn-color filter-btn history-filter-btn incident-history-custom-btn"
              (click)="setDateFilter('custom')">
              Custom
            </button>
          } @else {
            <div class="customDatepickerHistory">
              <div class="mat-mdc-form-field-flex">
                <div class="mat-mdc-form-field-infix">
                  <div class="mat-mdc-date-range-input">
                    <div
                      class="from-to-date-range-input-container"
                      (click)="rangePicker.open()"
                      (keydown.enter)="rangePicker.open()">
                      <div class="from-to-date-range-input-start-wrapper">
                        <input
                          id="status_main_incident_history_custom_start"
                          class="mat-mdc-date-range-input-inner custom-date-input"
                          [value]="customRange.start ? (customRange.start | date:'shortDate':undefined:locale) : ''"
                          readonly>
                      </div>
                      <span class="from-to-date-range-input-separator">-</span>
                      <div class="from-to-date-range-input-end-wrapper">
                        <input
                          id="status_main_incident_history_custom_end"
                          class="mat-mdc-date-range-input-inner custom-date-input"
                          [value]="customRange.end ? (customRange.end | date:'shortDate':undefined:locale) : ''"
                          readonly>
                      </div>
                      <div
                        class="mat-mdc-select-arrow-wrapper date-down-arrow"
                        (click)="rangePicker.open()"
                        (keydown.enter)="rangePicker.open()">
                        <div class="mat-mdc-select-arrow"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="customDatepicker">
                <div class="date-pick">
                  <mat-form-field appearance="fill">
                    <mat-date-range-input
                      [rangePicker]="rangePicker"
                      [min]="minDate"
                      [max]="maxDate"
                      [dateFilter]="customDateFilter">
                      <input
                        matStartDate
                        placeholder="Start date"
                        [(ngModel)]="customRange.start"
                        [ngModelOptions]="{standalone: true}"
                        (dateChange)="onCustomStartDateChange()">
                      <input
                        matEndDate
                        placeholder="End date"
                        [(ngModel)]="customRange.end"
                        [ngModelOptions]="{standalone: true}">
                    </mat-date-range-input>
                    <mat-datepicker-toggle matInfix [for]="rangePicker"></mat-datepicker-toggle>
                    <mat-date-range-picker
                      #rangePicker
                      [panelClass]="isDarkTheme ? ['status-main-date-range-panel', 'status-main-date-range-panel-dark'] : ['status-main-date-range-panel', 'status-main-date-range-panel-light']"
                      (closed)="applyCustomRange()">
                    </mat-date-range-picker>
                  </mat-form-field>
                </div>
              </div>
            </div>
          }
        </div>
        @if (incidentsLoading) {
          <div class="incident-history-skeleton-list" aria-hidden="true">
            @for (row of incidentHistorySkeletonRows; track trackByIndex($index)) {
              <div class="status-card incident-history-skeleton-card">
                <div class="incident-history-skeleton-main">
                  <div class="skeleton-line incident-history-skeleton-title"></div>
                  <div class="skeleton-line incident-history-skeleton-date"></div>
                </div>
                <div class="incident-history-skeleton-actions">
                  <div class="skeleton-pill incident-history-skeleton-pill"></div>
                  <div class="skeleton-line incident-history-skeleton-chevron"></div>
                </div>
              </div>
            }
          </div>
        }
        @if (!incidentsLoading) {
          @if (filteredIncidentHistoryItems.length > 0) {
            <mat-accordion class="status-accordion" multi>
              @for (incident of filteredIncidentHistoryItems; track trackByIncidentId($index, incident)) {
                <mat-expansion-panel
                  class="status-card incident-history-card"
                  (opened)="onIncidentHistoryToggle(incident, true)"
                  (closed)="onIncidentHistoryToggle(incident, false)">
                  <mat-expansion-panel-header>
                    <mat-panel-title>
                      <div class="card-title-row incident-history-header-row">
                        <div class="incident-history-title-stack">
                          <div class="status-card-title-main">
                            <span class="card-title">{{ incident?.title || 'Incident' }}</span>
                            <span class="status-pill" [ngClass]="getIncidentPillClass(incident)">
                              <span class="dot"></span>{{ getIncidentStatusLabel(incident) }}
                            </span>
                            @if (getIncidentImpactLabel(incident); as impactLabel) {
                              <span class="status-pill impact-pill incident-impact-tag" [ngClass]="getIncidentImpactClass(incident)">
                                <span class="dot status-dot"></span>{{ impactLabel }}
                              </span>
                            }
                          </div>
                          @if (!incident.expanded) {
                            <div class="card-meta status-card-summary-meta incident-history-summary-row">
                              <span><mat-icon class="material-symbols-outlined">calendar_today</mat-icon>{{ incident?.displayStartedDate || 'Date unavailable' }}</span>
                              <span><mat-icon class="material-symbols-outlined">schedule</mat-icon>{{ incident?.displayStartedTimeWithZone || 'Time unavailable' }}</span>
                              <span class="incident-history-affected-text">{{ getIncidentAffectedServicesLabel(incident) }}</span>
                            </div>
                          }
                        </div>
                      </div>
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <div class="card-body">
                    <div class="card-meta incident-history-meta incident-history-summary-row">
                      <span><mat-icon class="material-symbols-outlined">calendar_today</mat-icon>{{ incident?.displayStartedDate || 'Date unavailable' }}</span>
                      <span><mat-icon class="material-symbols-outlined">schedule</mat-icon>{{ incident?.displayStartedTimeWithZone || 'Time unavailable' }}</span>
                      <span class="incident-history-affected-text">{{ getIncidentAffectedServicesLabel(incident) }}</span>
                    </div>
                    <div
                      class="card-description history-description"
                      [class.line-clamp-4]="!incident.expanded"
                      [innerHTML]="formatRichText(incident?.description, 'No incident details available.') | statusPageRichText"
                      [matTooltip]="getTooltipText(incident?.description, 'No incident details available.')"
                      [matTooltipDisabled]="incident.expanded"
                      matTooltipPosition="above">
                    </div>
                    <div class="history-divider"></div>
                    @if (isIncidentHistoryLoading(incident)) {
                      <div class="history-detail-loader">
                        <div class="status-mini-loader" aria-hidden="true"></div>
                        <span>Loading incident details...</span>
                      </div>
                    }
                    @if (incident?.historyDetails?.timelineLoaded && hasIncidentTimeline(incident)) {
                      <div
                        class="timeline-block timeline-block-history"
                        >
                        <div class="timeline-title">Timeline</div>
                        <div class="timeline-list">
                          @for (update of getIncidentTimeline(incident); track trackByIncidentUpdateId($index, update)) {
                            <div
                              class="timeline-item"
                              [ngClass]="getTimelineUpdateClass(update)">
                              <div class="timeline-dot" [ngClass]="getTimelineUpdateClass(update)"></div>
                              <div class="timeline-body">
                                <div class="timeline-head">
                                  <span class="timeline-status" [ngClass]="getTimelineUpdateClass(update)">
                                    {{ update?.statusName || update?.status || 'Update' }}
                                  </span>
                                  <span class="timeline-time">{{ update?.displayTimestampWithZone || 'Time unavailable' }}</span>
                                </div>
                                <div
                                  class="timeline-message"
                                  [class.line-clamp-4]="!incident.expanded"
                                  [innerHTML]="formatRichText(update?.message, 'No update details available.') | statusPageRichText"
                                  [matTooltip]="getTooltipText(update?.message, 'No update details available.')"
                                  [matTooltipDisabled]="incident.expanded"
                                  matTooltipPosition="above">
                                </div>
                              </div>
                            </div>
                          }
                        </div>
                      </div>
                    }
                    @if (incident?.historyDetails?.timelineLoaded && !hasIncidentTimeline(incident)) {
                      <div
                        class="section-empty-state"
                        >
                        No timeline updates available.
                      </div>
                    }
                    @if (shouldShowIncidentPostmortem(incident)) {
                      <div class="history-divider"></div>
                      @if (incident?.historyDetails?.postmortem; as postmortem) {
                        <div class="postmortem-block">
                          <div class="postmortem-title-row">
                            <mat-icon class="material-symbols-outlined">description</mat-icon>
                            Post-Mortem
                          </div>
                          @if (hasPostmortemValue(postmortem?.summary)) {
                            <div class="postmortem-section">
                              <div class="postmortem-label">Summary</div>
                              <div class="postmortem-content" [innerHTML]="formatPostmortemContent(postmortem?.summary, 'No summary available.') | statusPageRichText"></div>
                            </div>
                          }
                          @if (hasPostmortemValue(postmortem?.rootCause)) {
                            <div class="postmortem-section">
                              <div class="postmortem-label">Root Cause</div>
                              <div class="postmortem-content" [innerHTML]="formatPostmortemContent(postmortem?.rootCause, 'No root cause available.') | statusPageRichText"></div>
                            </div>
                          }
                          @if (hasPostmortemValue(postmortem?.impact)) {
                            <div class="postmortem-section">
                              <div class="postmortem-label">Impact</div>
                              <div class="postmortem-content" [innerHTML]="formatPostmortemContent(postmortem?.impact, 'No impact details available.') | statusPageRichText"></div>
                            </div>
                          }
                          @if (hasPostmortemValue(postmortem?.resolution)) {
                            <div class="postmortem-section">
                              <div class="postmortem-label">Resolution</div>
                              <div class="postmortem-content" [innerHTML]="formatPostmortemContent(postmortem?.resolution, 'No resolution details available.') | statusPageRichText"></div>
                            </div>
                          }
                          @if (hasPostmortemValue(postmortem?.lessonsLearned)) {
                            <div class="postmortem-section">
                              <div class="postmortem-label">Lessons Learned</div>
                              <div class="postmortem-content" [innerHTML]="formatPostmortemContent(postmortem?.lessonsLearned, 'No lessons learned available.') | statusPageRichText"></div>
                            </div>
                          }
                          @if (hasPostmortemValue(postmortem?.actionItems)) {
                            <div class="postmortem-section">
                              <div class="postmortem-label">Action Items</div>
                              <div class="postmortem-content" [innerHTML]="formatPostmortemContent(postmortem?.actionItems, 'No action items available.') | statusPageRichText"></div>
                            </div>
                          }
                        </div>
                      }
                      @if (incident?.historyDetails?.postmortemLoaded && !incident?.historyDetails?.postmortem) {
                        <div
                          class="section-empty-state"
                          >
                          Post-mortem not available.
                        </div>
                      }
                    }
                  </div>
                </mat-expansion-panel>
              }
            </mat-accordion>
          }
          @if (incidentHistoryItems.length > 0 && filteredIncidentHistoryItems.length === 0) {
            <div class="section-empty-state">
              No incidents match your search.
            </div>
          }
          @if (incidentHistoryItems.length === 0) {
            <div class="section-empty-state">
              No incident history found.
            </div>
          }
        }
      </section>
      <div class="status-subfooter">
        <div class="status-shell status-subfooter-inner powered-only">
          <span class="powered-by-alertops">Powered by AlertOps</span>
        </div>
      </div>
    }
  }
</div>
